import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box } from "rizzui/box";
import { Text } from "rizzui/typography";
import { useDropzone } from "react-dropzone";
import PDFIcon from "../../assets/pdf";
import cn from "../../lib/utils/class-name";
import { Progressbar } from "rizzui/progressbar";
import BinIcon from "../../assets/bin";
import { ActionIcon } from "rizzui/action-icon";
import { Button } from "rizzui/button";
import { useModal } from "../../hooks/useModal";
import truncate from "../../lib/utils/truncate";
import { Input } from "rizzui/input";
import axios from "axios";
import { AuthContext } from "../../context/authContext";
import { baseUrl } from "../../modules/account/login";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotifcication } from "../../hooks/useNotification";
import { BsCheck } from "react-icons/bs";
import Upload2 from "../../assets/upload2";
import { set } from "react-hook-form";

export default function ImportMultipleFiles({ page = "", setUploaded }) {
  const { closeModal } = useModal();
  const [error, setError] = useState("");
  const [isDone, setIsDone] = useState<boolean[]>([]);
  const [fileNames, setFileNames] = useState<any[]>([]);
  const [progress, setProgress] = useState<any[]>([]);
  const [addedFiles, setAddedFiles] = useState<any[]>([]);
  const [blob, setBlob] = useState<Blob[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [named, setNamed] = useState<boolean[]>([]);
  const [uploadError, setUploadError] = useState("");
  const [uploadLoading, setUploadLoading] = useState(false);
  const [about, setAbout] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState<boolean[]>([true, true, true]);

  const queryClient = useQueryClient();
  const { view } = useModal();
  const { openNotification } = useNotifcication();

  const onDrop = useCallback(
    (acceptedFiles) => {
      setError("");
      setUploadError("");
      const oversizedFiles = acceptedFiles.filter(
        (file) => file.size > (2 * 1024 * 1024) / 2
      );
      if (oversizedFiles.length > 0) {
        setError("Maximum file size is to 1MB");
        return;
      }
      if (addedFiles.length + acceptedFiles.length > 3) {
        setError("You can only upload three(3) files at a time");
      } else {
        setAddedFiles((addedFiles) => addedFiles.concat(acceptedFiles));
        acceptedFiles.forEach((file, i) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onprogress = (e) => {
            let prog = Math.ceil((e.loaded / e.total) * 100);
            setProgress((progress) => [...progress, prog]);
          };
          reader.onload = (e) => {
            const fileResult = (e.currentTarget as FileReader)?.result;
            setIsActive(false);
            setIsDone((isDone) => [...isDone, true]);
          };
        });
      }
    },
    [addedFiles]
  );
  const navigate = useNavigate();
  const handleCancel = () => {
    setUploadError("");
    setError("");
    setIsDone([]);
    setFileNames([]);
    setProgress([]);
    setAddedFiles([]);
    setNamed([]);
    setAbout([]);
    setIsEditing([true, true, true]);
    setBlob([]);
    closeModal();
  };
  const { token } = useContext(AuthContext);
  const AuthStr = `Bearer ${token}`;
  const newKnowledgebase = (file, name, about) => {
    const formData = new FormData();
    formData.append("document_name", name);
    formData.append("knowledgebase_query", about);
    formData.append("knowledgebase_file", file);
    return axios
      .post(`${baseUrl}/knowledgebase/upload_new`, formData, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data;
        if (response?.data?.success) {
          queryClient.invalidateQueries({
            queryKey: ["knowledgebase", "getKnowledgebase"],
          });
          setUploaded();
        }

        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.statusCode === 401) navigate("/account/logout");
        else throw new Error("One or more files failed to upload");
      });
  };

  useEffect(() => {
    setIsActive(true);
    setUploadError("");
    setError("");
    setUploadError("");
    setIsDone([]);
    setProgress([]);
    setAddedFiles([]);
    setNamed([]);
    setFileNames([]);
    setAbout([]);
    setBlob([]);
    setIsEditing([true, true, true]);
  }, [view]);

  const handleSuccessError = (statuses) => {
    const errors: any[] = [];
    const isThereError = statuses.find((status) => status === "error");
    statuses.forEach((status, i) => {
      if (status !== "success") {
        errors.push(fileNames[i]);
      }
    });

    !isThereError && handleCancel();

    errors.length > 0 &&
      setUploadError(
        `${errors.join(" , ")} ${
          errors.length > 1 ? "were " : "was "
        } not uploaded`
      );

    isThereError
      ? openNotification({
          status: "error",
          message: `${errors.join(" , ")} ${
            errors.length > 1 ? "were " : "was "
          } not uploaded`,
        })
      : openNotification({
          status: "success",
          message: "Knowledge base added",
        });
  };

  const uploadAllFiles = async () => {
    const statuses: any = [];
    setUploadLoading(true);

    for (let i = 0; i < addedFiles.length; i++) {
      try {
        const name = fileNames[i];
        const aboutfile = about[i];
        await newKnowledgebase(addedFiles[i], name, aboutfile);
        statuses.push("success");
      } catch (error) {
        console.log(error);
        statuses.push("error");
      }
    }
    setUploadLoading(false);
    handleSuccessError(statuses);
  };

  const handleImport = () => {
    uploadAllFiles();
    if (page === "project") setUploaded(fileNames);
    //closeModal();
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleDelete = (i) => {
    const newFiles = [...addedFiles];
    const newNames = [...fileNames];
    const newNamed = [...named];
    const newAbout = [...about];
    const editing = [...isEditing];
    const done = [...isDone];
    newNamed.splice(i, 1);
    newNames.splice(i, 1);
    newFiles.splice(i, 1);
    newAbout.splice(i, 1);
    done.splice(i, 1);
    editing[i] = true;
    setIsEditing(editing);
    setIsDone(done);
    setError("");
    setUploadError("");
    setAddedFiles(newFiles);
    setNamed(newNamed);
    setFileNames(newNames);
    setIsActive(false);
    setAbout(newAbout);
  };

  const handleEdit = (i) => {
    const newisEditing = [...isEditing];
    newisEditing[i] = !newisEditing[i];
    setIsEditing(newisEditing);
  };

  useEffect(() => {
    addedFiles.length === 0 && setIsActive(true);
  }, [addedFiles.length]);

  const textStyle =
    "font-switzer font-[400] text-[14px] text-[#758599] leading-[18.48px]";
  const spanStyle = "font-[500] text-primary-dark underline";
  const buttonTextStyle =
    " w-[92px] h-[35px] rounded-[4px] text-[12px] font-gilroyMedium font-[400] leading-[14.56px]";

  return (
    <Box className="sm:w-[471px] w-[96%] mx-auto min-h-[274px] dark:bg-[#2A2C30] bg-background border-[#758599] border-[.5px] rounded-[8px] p-[32px] flex flex-col gap-[41px] text-primary-dark shadow-md">
      <button
        className="absolute top-4 right-4 p-1 rounded-lg z-10 text-gray-400 bg-transparent hover:text-gray-600 dark:hover:text-gray-300"
        onClick={closeModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="size-[18px]"
        >
          <path
            fillRule="evenodd"
            d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <div className="w-[325px]">
        <Text className="font-ibmPlexSerif font-[600] text-[18px] leading-[23.4px] mb-[16px]">
          Import file{"("}s{")"}
        </Text>
        <Text className={textStyle}>
          You can upload a maximum of 3 PDF files at a time
        </Text>
      </div>

      {addedFiles.length > 0 && (
        <div className="flex flex-col gap-[8px]">
          {addedFiles.map((file, i) => {
            return (
              <>
                {isEditing[i] ? (
                  <div
                    key={i}
                    className="border border-[#758599] border-dashed  rounded-[8px] flex flex-col place-content-center place-items-center justify-center min-h-[52px] h-auto py-[12px] px-[16px]"
                  >
                    <div className="w-full flex place-content-center place-items-center justify-center">
                      <Input
                        id={`this${i}`}
                        variant="outline"
                        placeholder="Give a name"
                        value={fileNames[i]}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFileNames((fileNames) => {
                            const newFileNames = [...fileNames];
                            newFileNames[i] = value;
                            return newFileNames;
                          });
                        }}
                        inputClassName="w-[126px] bg-inherit h-[29px] !py-0 rounded-[4px] border-[#758599] font-switzer text-[12px] font-[400]"
                        className="!border-0 !ring-0 !shadow-none"
                      />

                      <div className="grid grid-cols-8 gap-[4px] w-[90%] ml-[19px]">
                        <div className="col-span-1 place-content-center">
                          <PDFIcon
                            className={`${
                              isDone[i] ? "size-[24px]" : "size-[39px]"
                            } inline-block`}
                          />
                        </div>
                        <div className="col-span-7 items-center flex ">
                          <div className="w-full grid grid-cols-1 place-content-center">
                            <Text
                              className={cn(
                                textStyle,
                                "text-[12px] leading-[0px]",
                                isDone[i] ? "mb-0" : "mb-[12px]"
                              )}
                            >
                              {truncate(file.name, 25)}
                            </Text>
                            {!isDone[i] && (
                              <Progressbar
                                size="sm"
                                value={progress[i]}
                                trackClassName="dark:bg-btn"
                              />
                            )}
                          </div>

                          <div className="place-items-end">
                            <ActionIcon
                              variant="text"
                              onClick={() => {
                                handleDelete(i);
                              }}
                              className="size-[17px] !p-[0px]"
                            >
                              <BinIcon className="inline-block" />
                            </ActionIcon>

                            {!isDone[i] && (
                              <Text className={cn(textStyle, "text-[12px]")}>
                                {progress[i]}%
                              </Text>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {isDone[i] && (
                      <div className="flex flex-col gap-[8px] w-full mt-[24px]">
                        <Text className="text-[#758599] font-switzer text-[12px] font-[400]">
                          What’s "{fileNames[i]}" knowledge base about
                        </Text>
                        <Input
                          id={`about${i}`}
                          variant="outline"
                          value={about[i]}
                          onChange={(e) => {
                            const value = e.target.value;
                            setAbout((about) => {
                              const newAbout = [...about];
                              newAbout[i] = value;
                              return newAbout;
                            });
                          }}
                          placeholder="e.g It contains information about the company"
                          suffix={
                            <ActionIcon
                              className="!bg-transparent p-0 m-0 h-fit w-fit"
                              onClick={() => {
                                handleEdit(i);
                              }}
                            >
                              <Upload2
                                className={
                                  "dark:!text-[#758599] text-primary-dark"
                                }
                              />
                              <Text className="text-primary-dark ml-1 dark:!text-[#758599] font-switzer text-[12px] font-[400]">
                                Save
                              </Text>
                            </ActionIcon>
                          }
                          suffixClassName="justify-center item-center flex -mr-2"
                          inputClassName="w-full bg-inherit h-[29px] !py-0 rounded-[4px] border-[#758599] font-switzer text-[12px] font-[400]"
                          className="!border-0 !ring-0 !shadow-none"
                          onKeyUp={(e) => {
                            const target = e.target as HTMLInputElement;
                            e.key === "Enter" && handleEdit(i);
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    key={i}
                    className="border border-[#D9D9D9] rounded-[4px] flex flex-col place-content-center place-items-center justify-center min-h-[52px] h-auto py-[12px] px-[16px]"
                  >
                    <div className="w-full flex items-center justify-between">
                      <div className="w-[88%]">
                        <div className="flex w-full flex-col gap-[6px]">
                          <Text className="font-switzer text-[12px] truncate text-primary-dark">
                            {fileNames[i]}
                          </Text>
                          <Text className="font-switzer text-[12px] truncate text-[#758599]">
                            {about[i]}
                          </Text>
                        </div>
                      </div>

                      <div className="w-[12%] flex flex-row gap-1">
                        <ActionIcon
                          variant="text"
                          onClick={() => {
                            handleEdit(i);
                          }}
                          className="size-[17px] !p-[0px]"
                        >
                          <Text className="text-primary-dark underline text-[10px]">
                            Edit
                          </Text>
                        </ActionIcon>

                        <ActionIcon
                          variant="text"
                          onClick={() => {
                            handleDelete(i);
                          }}
                          className="size-[17px] !p-[0px]"
                        >
                          <BinIcon className="inline-block" />
                        </ActionIcon>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })}
        </div>
      )}

      {addedFiles.length < 3 && isActive && (
        <div
          className={cn(
            "border border-[#758599] border-dashed  rounded-[8px] flex place-content-center place-items-center justify-center h-[102px]",
            isDragActive ? "dark:bg-[#FFFFFF]/10 bg-[#000]/10" : "",
            error ? "border-red" : ""
          )}
        >
          <div
            {...getRootProps({
              onClick: () => {
                setError("");
              },
            })}
            className="min-w-[172px] place-content-center place-items-center text-center"
          >
            <div className="flex justify-center">
              <PDFIcon className={"text-center"} />
            </div>
            <Text className={cn(textStyle, "mt-[10px]")}>
              Drag and drop a pdf here <br />
              or click{" "}
              <span className={cn(spanStyle, "cursor-pointer")}>Browse</span> to
              select a pdf file
            </Text>
            <input {...getInputProps()} type="file" multiple={true} />
          </div>
        </div>
      )}

      {addedFiles.length < 3 && addedFiles.length > 0 && (
        <Button
          onClick={() => setIsActive(true)}
          className="!bg-transparent w-fit h-fit p-0 text-primary-dark font-switzer font-[500] text-[12px] -mt-[30px]"
        >
          + Add another file
        </Button>
      )}

      {error && (
        <Text
          className={cn(
            textStyle,
            "text-red !leading-[0px] -mt-[16px] text-center"
          )}
        >
          {error}
        </Text>
      )}
      {uploadError && (
        <Text
          className={cn(
            textStyle,
            "text-red !leading-[0px] -mt-[16px] text-center"
          )}
        >
          {uploadError}
        </Text>
      )}
      {addedFiles.length >= 1 && (
        <div className="flex justify-end gap-[20px]">
          <Button
            variant="text"
            className={cn("bg-muted", buttonTextStyle)}
            onClick={() => {
              handleCancel();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            className={cn(
              "bg-black dark:bg-btn !w-auto text-background hover:text-background",
              buttonTextStyle
            )}
            isLoading={uploadLoading}
            disabled={
              !(
                fileNames.length === addedFiles.length &&
                addedFiles.length === about.length &&
                fileNames.every((name) => name) &&
                about.every((desc) => desc)
              )
            }
            onClick={() => {
              handleImport();
            }}
          >
            Import now
          </Button>
        </div>
      )}
    </Box>
  );
}
